import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { IUseCase } from '@core/base/use-case.interface';
import { FtsAuthApiService } from '@core/services/api/fts-auth.api.service';
import { AuthStorageService } from '@core/services/auth-storage.service';
import { IAuthData } from '../../../modules/auth/interfaces/auth-data.interface';
import { IAuthRefreshTokenRequestDto } from '@core/interfaces/api/auth/auth-refresh-token-request-dto.interface';
import { IAuthResponseDto } from '@core/interfaces/api/auth/auth-response-dto.interface';

/**
 * Use-case for refreshing authentication tokens.
 * Implements the IUseCase interface with IRefreshTokenRequestDto as input and IAuthResponseDto as output.
 * This use case handles the API request to refresh an access token using a refresh token.
 * It will be consumed by the HTTP interceptor to refresh tokens when needed.
 */
@Injectable({
  providedIn: 'root',
})
export class RefreshTokenUseCaseService
  implements IUseCase<IAuthRefreshTokenRequestDto, IAuthResponseDto>
{
  private readonly authApiService = inject(FtsAuthApiService);
  private readonly authStorageService = inject(AuthStorageService);

  /**
   * Executes the refresh token use case.
   *
   * @param params The refresh token request data
   * @returns An Observable with the new authentication response containing tokens
   */
  execute(params: IAuthRefreshTokenRequestDto): Observable<IAuthResponseDto> {
    return this.authApiService.refreshToken(params).pipe(
      tap(response => {
        if (response.success) {
          const authData: IAuthData = {
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            expiresAt: response.expiresAt,
          };

          this.authStorageService.saveAuthData(authData);
        }
      })
    );
  }
}
